import React, { useCallback, useId, useState } from 'react'
import { CheckboxIndicator, Icon, l } from '@manychat/manyui'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { HintCard } from 'apps/aiAutomation/components/HintCard'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import {
  AiAlwaysOnAutomationsActions,
  AiAlwaysOnAutomationsSelectors,
} from 'apps/aiAutomation/store/core'
import { getIsGroupsFetched } from 'common/groups/selectors/groupsSelectors'
import { BotAssigneePicker } from 'common/settings/components/BotAssigneePicker'
import { OnChangeTargetProps } from 'common/settings/components/BotAssigneePicker/interfaces'
import { isBotAdminsReady } from 'common/settings/selectors/botSettingsSelectors'

import cm from './SetupTab.module.css'

export const SetupTab = () => {
  const dispatch = useAppDispatch()

  const isBotSettingsReady = useAppSelector(isBotAdminsReady)
  const isGroupsFetched = useAppSelector(getIsGroupsFetched)
  const selectedAssignee = useAppSelector(AiAlwaysOnAutomationsSelectors.getFallbackAssignee)

  const [isAssignmentEnabled, setAssignmentEnabled] = useState(Boolean(selectedAssignee))

  const handleSelect = (selection: Record<'target', OnChangeTargetProps>) => {
    AiAutomationAnalytics.logInstantAnswersAssigneeChanged({
      assignee: selection.target.group_id !== null ? 'group' : 'user',
    })

    dispatch(AiAlwaysOnAutomationsActions.updateFallbackAssigneeFx(selection.target))
  }

  const handleToggleAssignment = useCallback(() => {
    if (isAssignmentEnabled) {
      dispatch(
        AiAlwaysOnAutomationsActions.updateFallbackAssigneeFx({ user_id: null, group_id: null }),
      )

      AiAutomationAnalytics.logInstantAnswersAssigneeChanged({
        assignee: null,
      })
    }

    setAssignmentEnabled(!isAssignmentEnabled)
  }, [isAssignmentEnabled, dispatch, setAssignmentEnabled])

  const toggleId = useId()

  return (
    <article className="p-x p-y-md">
      <HintCard
        title={l.translate('AI will reply when')}
        icon="FbChat"
        text={l.translate('Someone asks a question in a DM or comments')}
      />

      <p className="text-body-sm text-disabled">
        {l.translate('AI answers based on the info you share with it')}
      </p>

      <p className="m-t-xl m-b text-subheading">
        {l.translate('When AI can’t find the knowledge to reply')}
      </p>

      <div className={cm.setupCard}>
        <label className={cm.setupCard__label}>
          <Icon.Assign />

          <span>{l.translate('Assign to a person')}</span>

          <span className={cm.setupCard__indicator}>
            <CheckboxIndicator
              id={toggleId}
              checked={isAssignmentEnabled}
              onChange={handleToggleAssignment}
              data-test-id="ai-automation/fallback-assignee-toggle"
            />
          </span>
        </label>

        {isAssignmentEnabled && (
          <div className={cm.setupCard__picker}>
            <BotAssigneePicker
              id="ai-automation/open-fallback-assignee-picker-button"
              value={selectedAssignee ?? undefined}
              fetchData={!isBotSettingsReady || !isGroupsFetched}
              onChange={handleSelect}
            />
          </div>
        )}
      </div>
    </article>
  )
}
